/**
 * Shared portfolio calculations.
 *
 * Holdings store only raw facts (shares, costBasis, currentPrice, dividends).
 * Everything derivable - market value, gain/loss, portfolio % and the summary
 * totals - is computed here so the Portfolio table, Dashboard and Fund Admin
 * all agree on a single source of truth.
 */

/** Legacy summary row names stored as holdings before normalization. */
export const SUMMARY_HOLDING_NAMES = ['equities total', 'cash', 'portfolio value'];

export const holdingDisplayName = (holding) =>
  (holding?.name ?? holding?.equities ?? '').trim();

export const isSummaryHolding = (holding) =>
  SUMMARY_HOLDING_NAMES.includes(holdingDisplayName(holding).toLowerCase());

/** Equity positions only — excludes legacy Cash / total rows. */
export const positionHoldings = (holdings = []) =>
  holdings.filter((h) => !isSummaryHolding(h));

/**
 * Coerce a loose value (string like "$1,234", "13.9%", "(24%)", "--", or a
 * number) into a number, or null when it is blank / not a number.
 */
export const toNumber = (value) => {
  if (value === null || value === undefined) return null;
  if (typeof value === 'number') return Number.isNaN(value) ? null : value;

  const raw = String(value).trim();
  if (raw === '' || raw === '--') return null;

  // Parenthesized values represent negatives, e.g. "(24%)" -> -24
  const isNegative = /^\(.*\)$/.test(raw);
  const cleaned = raw.replace(/[$,%()\s]/g, '');
  if (cleaned === '') return null;

  const n = Number(cleaned);
  if (Number.isNaN(n)) return null;
  return isNegative ? -n : n;
};

/**
 * Market value of a single holding.
 * Prefers shares x currentPrice; falls back to a directly-entered marketValue
 * (used for legacy rows that predate share-count tracking).
 */
export const holdingMarketValue = (holding) => {
  if (!holding) return 0;
  const shares = toNumber(holding.shares);
  const price = toNumber(holding.currentPrice);
  if (shares !== null && price !== null) return shares * price;

  const mv = toNumber(holding.marketValue);
  return mv !== null ? mv : 0;
};

/** Total dividends (cash + reinvested) for a holding. */
export const holdingDividends = (holding) =>
  (toNumber(holding?.cashDividends) || 0) + (toNumber(holding?.reinvestedDividends) || 0);

/**
 * Price gain/loss percentage for a holding: (market value − cost basis) / cost basis.
 * Dividends are not added — legacy dividend columns often double-count vs market value.
 * Returns null when there is no cost basis to compare against.
 */
export const holdingGainLossPct = (holding) => {
  const cost = toNumber(holding?.costBasis);
  if (!cost) return null;
  const mv = holdingMarketValue(holding);
  return ((mv - cost) / cost) * 100;
};

/** Aggregate raw totals across all (non-summary) equity holdings. */
export const equitiesTotals = (holdings = []) =>
  holdings.reduce(
    (acc, h) => {
      acc.costBasis += toNumber(h.costBasis) || 0;
      acc.marketValue += holdingMarketValue(h);
      acc.cashDividends += toNumber(h.cashDividends) || 0;
      acc.reinvestedDividends += toNumber(h.reinvestedDividends) || 0;
      return acc;
    },
    { costBasis: 0, marketValue: 0, cashDividends: 0, reinvestedDividends: 0 }
  );

/** Total portfolio value = equities market value + cash. */
export const portfolioValue = (holdings = [], cash = 0) =>
  equitiesTotals(holdings).marketValue + (toNumber(cash) || 0);

/** A single holding's share of the total portfolio, as a percentage. */
export const portfolioPercent = (holding, holdings = [], cash = 0) => {
  const total = portfolioValue(holdings, cash);
  if (!total) return null;
  return (holdingMarketValue(holding) / total) * 100;
};

/**
 * Build the derived summary rows (Equities Total, Cash, Portfolio Value) that
 * the Portfolio table appends beneath the individual holdings.
 * @param {number|null} contributedCapital — fund-level LP capital for the Portfolio Value row
 */
export const summaryRows = (holdings = [], cash = 0, contributedCapital = null) => {
  const totals = equitiesTotals(holdings);
  const cashValue = toNumber(cash) || 0;
  const total = totals.marketValue + cashValue;
  const pct = (value) => (total ? (value / total) * 100 : null);

  const equitiesGainLoss = totals.costBasis
    ? ((totals.marketValue - totals.costBasis) / totals.costBasis) * 100
    : null;

  const fundCapital = toNumber(contributedCapital);
  const portfolioCostBasis =
    fundCapital !== null && fundCapital > 0 ? fundCapital : totals.costBasis;

  return [
    {
      name: 'Equities Total',
      isSummary: true,
      costBasis: totals.costBasis,
      marketValue: totals.marketValue,
      cashDividends: totals.cashDividends,
      reinvestedDividends: totals.reinvestedDividends,
      portfolioPercent: pct(totals.marketValue),
      gainLossPct: equitiesGainLoss,
    },
    {
      name: 'Cash',
      isSummary: true,
      marketValue: cashValue,
      portfolioPercent: pct(cashValue),
      gainLossPct: null,
    },
    {
      name: 'Portfolio Value',
      isSummary: true,
      costBasis: portfolioCostBasis,
      marketValue: total,
      portfolioPercent: 100,
      gainLossPct: portfolioCostBasis
        ? ((total - portfolioCostBasis) / portfolioCostBasis) * 100
        : null,
    },
  ];
};
